import Badge from '@components/Badge';
import Column from '@components/Column';
import Image from '@components/Image';
import Text from '@components/Text';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import useGlobalName from '@hooks/useGlobalName';
import { ShopRecommendedItemRow } from '@models/item/recommendedItem';
import { useFlow } from '@stackflow/react';
import { pressActionEffect } from '@styles/mixin';

interface Props {
    shopId: string;
    item: ShopRecommendedItemRow;
}

function RecommendationItem({ item, shopId }:Props) {
    const {
        thumbnailUrl, name, salePrice, shopItemId, badges, nameKo, nameJa, nameEn,
    } = item;
    const itemName = useGlobalName({
        nameKo, nameJa, nameEn, name,
    });
    const priceUnit = usePriceUnit();
    const { push } = useFlow();

    const handleChangeItem = () => {
        push('ItemActivity', { itemId: shopItemId, shopId });
    };

    return (
        <Container spacing={2}
            aria-label='recommendation-item'
            data-item-name={itemName}
            data-item-id={shopItemId}
            data-price={salePrice}
            align='center'
            onClick={handleChangeItem}
            css={pressActionEffect} >
            <Thumbnail src={thumbnailUrl}
                alt={itemName}
                width={96}
                height={96} />
            <Column align='center' fullWidth>
                <NameText size='body-long' align='center'>{itemName}</NameText>
                <Text size='title' fontWeight='medium' align='center'>
                    {salePrice.toLocaleString()}{priceUnit}
                </Text>
            </Column>
            {badges.length > 0 && (
                <Badges>
                    {badges.map((badge, index) => (
                        <Badge key={index.toString()}>{badge}</Badge>
                    ))}
                </Badges>
            )}
        </Container>
    );
}

const Container = styled(Column)`
    flex-shrink: 0;
    scroll-snap-align: start;
    width: 96px;
`;

const Thumbnail = styled(Image)`
    aspect-ratio: 1;
    border-radius: var(--radius-xs);
    object-fit: cover;
    width: 100%;
`;

const NameText = styled(Text)`
    word-break: break-word;
`;

const Badges = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-1);
    justify-content: center;
    width: 100%;
`;

export default RecommendationItem;
